import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { IssuePriority, IssueStatus } from '@d-orca/shared/trusstor';
import type { IssuesViewMode, IssuesFilterState } from '@utils/issues';

export type IssuesState = {
  siteId: string | null;
  viewMode: IssuesViewMode;
  filters: IssuesFilterState;
};

const initialFilters: IssuesFilterState = {
  priorities: [],
  statuses: [],
  safetyOnly: false,
  professionIds: [],
};

const initialState: IssuesState = {
  siteId: null,
  viewMode: 'professions',
  filters: initialFilters,
};

function toggle<T>(arr: T[], v: T): T[] {
  const idx = arr.indexOf(v);
  if (idx === -1) return [...arr, v];
  const copy = arr.slice();
  copy.splice(idx, 1);
  return copy;
}

const slice = createSlice({
  name: 'issues',
  initialState,
  reducers: {
    resetForSite(state, action: PayloadAction<string>) {
      // No-op when re-mounting onto the same site (e.g., navigating away
      // to /portfolio and back). Preserving the existing filter/view
      // state across remounts prevents a visible "filtered → unfiltered →
      // filtered" jump on the issues page; the URL→Redux mirror would
      // otherwise have to re-dispatch the same values after a paint with
      // the cleared state.
      if (state.siteId === action.payload) return;
      Object.assign(state, initialState, { siteId: action.payload });
    },
    setViewMode(state, action: PayloadAction<IssuesViewMode>) {
      state.viewMode = action.payload;
    },
    togglePriority(state, action: PayloadAction<IssuePriority>) {
      state.filters.priorities = toggle(state.filters.priorities, action.payload);
    },
    toggleStatus(state, action: PayloadAction<IssueStatus>) {
      state.filters.statuses = toggle(state.filters.statuses, action.payload);
    },
    setSafetyOnly(state, action: PayloadAction<boolean>) {
      state.filters.safetyOnly = action.payload;
    },
    toggleProfession(state, action: PayloadAction<string>) {
      state.filters.professionIds = toggle(state.filters.professionIds, action.payload);
    },
    clearFilters(state) {
      state.filters = { ...initialFilters };
    },
    setPriorityFilters(state, action: PayloadAction<IssuePriority[]>) {
      state.filters.priorities = action.payload;
    },
    setStatusFilters(state, action: PayloadAction<IssueStatus[]>) {
      state.filters.statuses = action.payload;
    },
    setProfessionFilters(state, action: PayloadAction<string[]>) {
      state.filters.professionIds = action.payload;
    },
  },
});

export const {
  resetForSite,
  setViewMode,
  togglePriority,
  toggleStatus,
  setSafetyOnly,
  toggleProfession,
  clearFilters,
  setPriorityFilters,
  setStatusFilters,
  setProfessionFilters,
} = slice.actions;

export const issuesReducer = slice.reducer;
